'use client'

import { createContext, useContext, ReactNode } from 'react'

import { useWordHunt } from '../hooks/useWordHunt'
import type { GameConfig, GameActions } from '../types'

/**
 * Interface simplificada do contexto do jogo WordHunt
 * Seguindo princípios de Clean Code: interfaces pequenas e focadas
 */
interface WordHuntContextValue {
  // Estado do jogo (apenas o necessário para os componentes)
  currentWord: string
  foundWords: string[]
  gameBoard: any // TODO: Melhorar tipagem com a biblioteca
  gameStats: any // TODO: Melhorar tipagem com a biblioteca
  isComplete: boolean
  foundLetterPositions: { row: number; column: number }[]
  selectedPositions: { row: number; column: number }[] // Posições selecionadas na ordem
  selectionError: boolean // Indica erro temporário na seleção
  selectionSuccess: boolean // Indica sucesso temporário na seleção

  // Ações do jogo
  handleSquareClick: (position: { row: number; column: number }) => void
  handleSquareEnter: (position: { row: number; column: number }) => void
  handleSelectionEnd: () => void

  // Utilitários
  isPositionActive: (position: { row: number; column: number }) => boolean
  isPositionFound: (position: { row: number; column: number }) => boolean
}

/**
 * Props do provider
 */
interface WordHuntProviderProps {
  children: ReactNode
  config: GameConfig
  actions: GameActions
}

/**
 * Contexto do jogo WordHunt
 */
const WordHuntContext = createContext<WordHuntContextValue | undefined>(
  undefined
)

/**
 * Provider simplificado do jogo WordHunt
 * Usa o hook personalizado para toda a lógica de negócio
 */
export function WordHuntProvider({
  children,
  config,
  actions
}: WordHuntProviderProps) {
  const {
    gameState,
    gameBoard,
    gameStats,
    isComplete,
    foundLetterPositions,
    selectionError,
    selectionSuccess,
    handleSquareClick,
    handleSquareEnter,
    handleSelectionEnd,
    isPositionActive,
    isPositionFound
  } = useWordHunt(config, actions)

  const contextValue: WordHuntContextValue = {
    currentWord: gameState.currentWord,
    foundWords: gameState.foundWords,
    gameBoard,
    gameStats,
    isComplete,
    foundLetterPositions,
    selectedPositions: gameState.selectedPositions,
    selectionError,
    selectionSuccess,
    handleSquareClick,
    handleSquareEnter,
    handleSelectionEnd,
    isPositionActive,
    isPositionFound
  }

  return (
    <WordHuntContext.Provider value={contextValue}>
      {children}
    </WordHuntContext.Provider>
  )
}

/**
 * Hook para acessar o contexto do jogo WordHunt
 * Com validação de uso dentro do provider
 */
export function useWordHuntContext(): WordHuntContextValue {
  const context = useContext(WordHuntContext)

  if (context === undefined) {
    throw new Error('useWordHuntContext must be used within a WordHuntProvider')
  }

  return context
}
